import { useEffect, useMemo, useState } from 'react';
import PageHeader from '../../../components/owner/PageHeader';
import {
  ColumnFilterHeader,
  formatPhoneFilterValue,
  formatDateFilterValue,
  matchTextFilter,
  useColumnFilters,
} from '../../../components/admin/ColumnFilterHeader';
import { matchSaleDateRange, matchSaleHistoryDateFilter } from '../../receptionist/sales/saleHistoryFilters';
import { usePanelToast } from '../../../context/PanelToastContext';
import PanelUserForm from './PanelUserForm';
import { createPanelUserExporter } from './exportPanelUsers';
import PrintActionsHeader from '../../../components/shared/PrintActionsHeader';
import {
  fetchPanelUsers,
  createPanelUser,
  updatePanelUser,
  deletePanelUser,
} from '../../../services/owner/panelUsersApi';
import '../../owner/ownerPages.css';
import '../../owner/subscriptions/subscriptions.css';
import '../../receptionist/members/members.css';
import '../../admin/adminListPages.css';
import './panelUsers.css';

function StatusBadge({ status }) {
  const isActive = status === 'active';
  return (
    <span className={`owner-badge ${isActive ? 'owner-badge-success' : 'owner-badge-warning'}`}>
      {isActive ? 'Active' : 'Suspended'}
    </span>
  );
}

function panelUserStatusLabel(status) {
  return status === 'active' ? 'Active' : 'Suspended';
}

function PanelUserManagement({
  panelLabel,
  subtitle,
  initialUsers,
  exportSlug,
  panel,
}) {
  const toast = usePanelToast();
  const useApi = Boolean(panel);
  const exporter = useMemo(
    () => createPanelUserExporter({
      title: `${panelLabel} Users`,
      slug: exportSlug,
      brand: panel === 'owner' ? 'owner' : undefined,
    }),
    [panelLabel, exportSlug, panel],
  );

  const [users, setUsers] = useState(initialUsers || []);
  const {
    filters,
    updateFilter,
    openFilterKey,
    toggleFilter,
  } = useColumnFilters({
    name: '',
    email: '',
    phone: '',
    panelAccess: '',
    joined: '',
    lastLogin: '',
    status: '',
  });
  const [showAddModal, setShowAddModal] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [formKey, setFormKey] = useState(0);
  const [loading, setLoading] = useState(useApi);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');

  useEffect(() => {
    if (!useApi) return undefined;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const data = await fetchPanelUsers(panel);
        if (!cancelled) setUsers(data);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load users');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [panel, useApi]);

  const filteredUsers = useMemo(() => (
    users.filter((user) => (
      matchSaleDateRange(user.joinedDate, rangeStart, rangeEnd)
      && matchTextFilter(user.name, filters.name)
      && matchTextFilter(user.email, filters.email)
      && matchTextFilter(user.phone, filters.phone)
      && matchTextFilter(panelLabel, filters.panelAccess)
      && matchSaleHistoryDateFilter(user.joinedDate, filters.joined)
      && matchTextFilter(user.lastLogin, filters.lastLogin)
      && matchTextFilter(panelUserStatusLabel(user.status), filters.status)
    ))
  ), [users, filters, panelLabel, rangeStart, rangeEnd]);

  const openAddModal = () => {
    setFormKey((k) => k + 1);
    setShowAddModal(true);
  };

  const closeAddModal = () => setShowAddModal(false);

  const openEditModal = (user) => {
    setFormKey((k) => k + 1);
    setEditTarget(user);
  };

  const closeEditModal = () => setEditTarget(null);

  const handleAddSubmit = async (form) => {
    if (useApi) {
      try {
        setSaving(true);
        const created = await createPanelUser(panel, form);
        setUsers((prev) => [created, ...prev]);
        closeAddModal();
        toast.showSaved('User');
      } catch (err) {
        toast.showError(err.message || 'Failed to add user');
      } finally {
        setSaving(false);
      }
      return;
    }

    const today = new Date().toISOString().slice(0, 10);
    const newUser = {
      id: String(Date.now()),
      name: form.name,
      email: form.email,
      phone: form.phone,
      role: 'Staff',
      status: form.status,
      joinedDate: today,
      lastLogin: '—',
    };
    setUsers((prev) => [...prev, newUser]);
    closeAddModal();
  };

  const handleEditSubmit = async (form) => {
    if (!editTarget) return;

    if (useApi) {
      try {
        setSaving(true);
        const updated = await updatePanelUser(editTarget.id, form);
        setUsers((prev) => prev.map((user) => (user.id === editTarget.id ? updated : user)));
        closeEditModal();
        toast.showSaved('User');
      } catch (err) {
        toast.showError(err.message || 'Failed to update user');
      } finally {
        setSaving(false);
      }
      return;
    }

    setUsers((prev) =>
      prev.map((user) =>
        user.id === editTarget.id
          ? {
              ...user,
              name: form.name,
              email: form.email,
              phone: form.phone,
              status: form.status,
            }
          : user,
      ),
    );
    closeEditModal();
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    if (useApi) {
      try {
        setSaving(true);
        await deletePanelUser(deleteTarget.id);
        setUsers((prev) => prev.filter((user) => user.id !== deleteTarget.id));
        setDeleteTarget(null);
        toast.showDeleted('User');
      } catch (err) {
        toast.showError(err.message || 'Failed to delete user');
      } finally {
        setSaving(false);
      }
      return;
    }

    setUsers((prev) => prev.filter((user) => user.id !== deleteTarget.id));
    setDeleteTarget(null);
  };

  return (
    <div>
      <PageHeader title="User Management" subtitle={subtitle}>
        <button type="button" className="owner-btn owner-btn-primary" onClick={openAddModal} disabled={loading || saving}>
          Add User
        </button>
      </PageHeader>

      <PrintActionsHeader
        startDate={rangeStart}
        endDate={rangeEnd}
        onStartDateChange={setRangeStart}
        onEndDateChange={setRangeEnd}
        onClearDates={() => { setRangeStart(''); setRangeEnd(''); }}
        onPrint={() => exporter.print(filteredUsers)}
        onCsv={() => exporter.csv(filteredUsers)}
        onPdf={() => exporter.pdf(filteredUsers)}
        onExcel={() => exporter.excel(filteredUsers)}
        onExport={() => exporter.export(filteredUsers)}
        ariaLabel={`${panelLabel} user print and export`}
      />

      {loading && <p className="owner-placeholder-text owner-mb-16">Loading users...</p>}
      {error && <p className="owner-placeholder-text owner-mb-16" style={{ color: 'var(--owner-danger, #dc2626)' }}>{error}</p>}

      <div className="owner-card rec-panel-card rec-member-list-card admin-list-table-card">
        <div className="owner-table-wrap">
          <table className="owner-table rec-member-table admin-list-table">
            <thead>
              <tr>
                <ColumnFilterHeader
                  label="Name"
                  filterKey="name"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search name..."
                />
                <ColumnFilterHeader
                  label="Email"
                  filterKey="email"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search email..."
                />
                <ColumnFilterHeader
                  label="Phone"
                  filterKey="phone"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="e.g. 0300-1234567"
                  formatValue={formatPhoneFilterValue}
                />
                <ColumnFilterHeader
                  label="Panel Access"
                  filterKey="panelAccess"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search panel..."
                />
                <ColumnFilterHeader
                  label="Joined"
                  filterKey="joined"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="DD-MM-YYYY"
                  formatValue={formatDateFilterValue}
                />
                <ColumnFilterHeader
                  label="Last Login"
                  filterKey="lastLogin"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search last login..."
                />
                <ColumnFilterHeader
                  label="Status"
                  filterKey="status"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search status..."
                />
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length > 0 ? (
                filteredUsers.map((user) => (
                  <tr key={user.id}>
                    <td>{user.name}</td>
                    <td>{user.email}</td>
                    <td>{user.phone}</td>
                    <td>{panelLabel}</td>
                    <td>{user.joinedDate}</td>
                    <td>{user.lastLogin || '—'}</td>
                    <td><StatusBadge status={user.status} /></td>
                    <td>
                      <div className="panel-user-actions-cell">
                        <button
                          type="button"
                          className="owner-btn owner-btn-secondary owner-btn-sm"
                          onClick={() => openEditModal(user)}
                          disabled={saving}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="owner-btn owner-btn-sm panel-user-btn-danger"
                          onClick={() => setDeleteTarget(user)}
                          disabled={saving}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} className="owner-placeholder-text" style={{ textAlign: 'center', padding: '24px' }}>
                    {loading ? 'Loading...' : 'No users found. Add a user to get started.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showAddModal && (
        <div className="subscription-modal-overlay" onClick={closeAddModal}>
          <div
            className="subscription-modal admin-form-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="add-panel-user-title"
          >
            <div className="subscription-modal-header">
              <h2 id="add-panel-user-title" className="subscription-modal-title">
                Add {panelLabel} User
              </h2>
              <button type="button" className="subscription-modal-close" onClick={closeAddModal} aria-label="Close">×</button>
            </div>
            <PanelUserForm
              key={formKey}
              submitLabel="Add User"
              onSubmit={handleAddSubmit}
              onCancel={closeAddModal}
            />
          </div>
        </div>
      )}

      {editTarget && (
        <div className="subscription-modal-overlay" onClick={closeEditModal}>
          <div
            className="subscription-modal admin-form-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="edit-panel-user-title"
          >
            <div className="subscription-modal-header">
              <h2 id="edit-panel-user-title" className="subscription-modal-title">
                Edit {panelLabel} User
              </h2>
              <button type="button" className="subscription-modal-close" onClick={closeEditModal} aria-label="Close">×</button>
            </div>
            <PanelUserForm
              key={formKey}
              initialData={editTarget}
              submitLabel="Save Changes"
              isEdit
              onSubmit={handleEditSubmit}
              onCancel={closeEditModal}
            />
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="subscription-modal-overlay" onClick={() => setDeleteTarget(null)}>
          <div className="subscription-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="subscription-modal-header">
              <h2 className="subscription-modal-title">Delete User</h2>
              <button type="button" className="subscription-modal-close" onClick={() => setDeleteTarget(null)} aria-label="Close">×</button>
            </div>
            <p className="panel-user-modal-message">
              Delete <strong>{deleteTarget.name}</strong> ({deleteTarget.email})? They will lose {panelLabel} access immediately.
            </p>
            <div className="subscription-form-actions">
              <button type="button" className="owner-btn panel-user-btn-danger" onClick={handleDelete} disabled={saving}>
                {saving ? 'Deleting…' : 'Delete'}
              </button>
              <button type="button" className="owner-btn owner-btn-secondary" onClick={() => setDeleteTarget(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PanelUserManagement;
